/**
 * The land-cover bar chart that CSqueeze and EUSCoast both show for a transect.
 * The two modules measure the same five covers in the same order and draw them the same way; only
 * the title and the business rule underneath differ. Keeping the drawing here is the same decision
 * already taken for their transect analysis, their GeoJSON and their PDF report - and for the same
 * reason: every improvement made to one of these charts so far has landed in one module and not
 * the other, and a reader comparing the two finds one of them stale.
 * @module LAND_COVER_CHART
 */

/**
 * @constant MIN_LABEL_SHARE
 * @description How much of the bar a slice has to take up before its number is written on it.
 * The chart is stacked to 100%, so a land cover holding two per cent of the transect is a couple of
 * pixels wide - the number written on it has nowhere to go and lands on its neighbours. Below this
 * the number is left out and the hover tells the whole story instead.
 * @memberof module:LAND_COVER_CHART
 */
export const MIN_LABEL_SHARE = 0.08;

/**
 * @constant CHART_COLUMNS
 * @description The five land covers, in the order the projection table lays them out.
 * @memberof module:LAND_COVER_CHART
 */
export const CHART_COLUMNS = [1, 2, 3, 4, 5];

/**
 * @constant LAND_COVER_COLORS
 * @description One colour per land cover, in the order of {@link CHART_COLUMNS}: human
 * intervention, vegetation, mangrove, water, beach.
 * @memberof module:LAND_COVER_CHART
 */
export const LAND_COVER_COLORS = ['#af2a2a', '#129912', '#687537', '#0000ff', '#e3c565'];

/**
 * @function drawLandCoverChart
 * @description Draws the land-cover distribution as a 100%-stacked bar chart, one colour per
 * category, with each slice's measurement written on it - where it fits.
 * @param {Object} data - Google Charts DataTable.
 * @param {HTMLElement} node - DOM node to draw into.
 * @param {Object} options
 * @param {String} options.title - The chart's title, already translated.
 * @param {Array<String>} [options.colors] - Overrides {@link LAND_COVER_COLORS}.
 * **Called by:
 * @see {TransectResult} - CSqueeze's and EUSCoast's transect panels.
 * @memberof module:LAND_COVER_CHART
 * @returns {void}
 */
export const drawLandCoverChart = (data, node, { title, colors = LAND_COVER_COLORS }) => {
  /**
   * @function rowTotal
   * @description Everything measured along a transect in one year, which is what the bar is
   * divided into.
   * @param {Number} row - The row index.
   * @memberof module:LAND_COVER_CHART
   * @returns {Number} The sum of the five land covers.
   */
  const rowTotal = (row) =>
    CHART_COLUMNS.reduce((total, column) => {
      const value = data.getValue(row, column);
      return total + (Number.isFinite(value) ? value : 0);
    }, 0);

  /**
   * @function share
   * @description How much of that year's transect one land cover holds, which is what decides
   * whether its number is written on the chart.
   * @param {Number} row - The row index.
   * @param {Number} column - The land cover's column.
   * @memberof module:LAND_COVER_CHART
   * @returns {Number} A fraction between 0 and 1.
   */
  const share = (row, column) => {
    const total = rowTotal(row);
    const value = data.getValue(row, column);
    return total > 0 && Number.isFinite(value) ? value / total : 0;
  };

  /**
   * @function labelFor
   * @description The number written on a slice, or nothing when the slice is too narrow to hold
   * it. Rounded to whole metres: the decimals were what made the labels long enough to collide in
   * the first place, and the exact figure is in the hover and in the table below.
   * @param {Number} column - The land cover's column.
   * @memberof module:LAND_COVER_CHART
   * @returns {Object} A DataView column definition.
   */
  const labelFor = (column) => ({
    calc: (table, row) => {
      const value = table.getValue(row, column);
      if (!Number.isFinite(value) || share(row, column) < MIN_LABEL_SHARE) return null;
      return String(Math.round(value));
    },
    type: 'string',
    role: 'annotation',
  });

  const view = new window.google.visualization.DataView(data);
  view.setColumns([0, ...CHART_COLUMNS.flatMap((column) => [column, labelFor(column)])]);

  const options = {
    title,
    width: 445,
    height: 250,
    bar: { groupWidth: '70%' },
    isStacked: 'percent',
    legend: { position: 'none' },
    hAxis: { minValue: 0, ticks: [0, 0.25, 0.5, 0.75, 1] },
    /**
     * Hovering anywhere on a year's bar reports every land cover at once, rather than only the
     * slice under the pointer. A slice too narrow to hold its number is also too narrow to aim at,
     * so per-slice hovering would have hidden exactly the figures this is meant to recover.
     * The wording is left to the chart: in this mode it already writes the land cover's name and
     * its measurement for every slice, so a tooltip of our own only said the same things twice.
     */
    focusTarget: 'category',
    annotations: {
      alwaysOutside: false,
      textStyle: { fontSize: 11 },
      stem: { length: 0 },
    },
    series: Object.fromEntries(colors.map((color, index) => [index, { color }])),
  };

  const chart = new window.google.visualization.BarChart(node);
  chart.draw(view, window.google.charts.Bar.convertOptions(options));
  node.style.overflow = 'hidden';
};
